import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { useNavigate } from "react-router-dom";
import Spinner from "../../components/Spinner/spinner";
import {
  deleteTimeSheetDetails,
  getTimesheets,
} from "../../services/viewTimeSheet";
import {
  approveTimesheet,
  downloadExcel,
  rejectTimesheet,
} from "../../services/timeSheetApproval";
import { format } from "date-fns";
import "./timeSheetList.css";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { appInsights } from "../../AppInsights";
import { Tooltip } from "primereact/tooltip";

const TimeSheetList = ({
  employeeId,
  selectedMonth,
  projectType,
  year,
  periodTypeId,
}) => {
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [timeSheetData, setTimeSheetData] = useState([]);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const currentUserTypeId = localStorage.getItem("userTypeId");
  const navigate = useNavigate();
  const toast = React.useRef(null);
  const { userData } = useSelector((state) => state?.user);
  const [visible, setVisible] = useState(false);
  const [showDownloadIcons, setShowDownloadIcons] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const userId = userData?.userId;
  const userType = localStorage.getItem("userType");
  const userTypeRole = userData?.userTypes?.find((type) =>
    type.userType.toLowerCase().includes(userType?.toLowerCase())
  );
  const isManagerViewingEmployee =
    [1, 2, 3, 4].includes(userTypeRole?.userTypeRoleId) && employeeId;
  const isManagerViewingOwnTimesheet =
    [1, 2, 3, 4].includes(userTypeRole?.userTypeRoleId) && !employeeId;
  const isHR =
    userTypeRole?.userTypeRoleId === 2 || userTypeRole?.userTypeRoleId === 3;
  const OnlyHr = userTypeRole?.userTypeRoleId === 2;
  const months = [
    { label: "All Months", value: "all" },
    { label: "January", value: 1 },
    { label: "February", value: 2 },
    { label: "March", value: 3 },
    { label: "April", value: 4 },
    { label: "May", value: 5 },
    { label: "June", value: 6 },
    { label: "July", value: 7 },
    { label: "August", value: 8 },
    { label: "September", value: 9 },
    { label: "October", value: 10 },
    { label: "November", value: 11 },
    { label: "December", value: 12 },
  ];

  const downloadBtn = () => {
    const paginatorRight = isHR ? (
      <div className="flex justify-content-start">
        <Button
          type="button"
          icon="pi pi-download"
          label="Download"
          className="p-button-outlined border-1 border-round surface-border"
          onClick={() => {
            handleDownloadExcel(year, selectedMonth, projectType, employeeId);
          }}
        />
      </div>
    ) : null;

    return paginatorRight;
  };

  const handleDownloadExcel = async (year, month, userTypeId, employeeId) => {
    try {
      if (typeof year !== "number" || typeof month !== "number") {
        //  TODO: add toast
        toast?.current?.show({
          severity: "error",
          summary: "Error",
          detail: `Please select a year and month before downloading.`,
          life: 3000,
        });
        return;
      }
      const monthName = months?.filter((x) => x?.value === month);
      // TODO : include:employeeId
      const { url, filename } = await downloadExcel(
        year,
        month,
        userTypeId,
        monthName[0]?.label
      );

      // Create a temporary <a> element to trigger the download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename); // Set the filename from the response header
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Clean up the Blob URL after the download
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      //  TODO: add toast
      appInsights.trackException("Download Excel timeSheetList", {
        exception: error,
      });

      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.message
          ? error?.message
          : error?.status === 400
          ? "Timesheet entries not found for the given month or year"
          : error?.status === 500
          ? "Internal Server Error"
          : error?.status === 404
          ? "User not Found"
          : "Error occurred while submitting details",
        life: 5000,
      });
    }
  };

  const formatDateForDisplay = (date) => {
    return format(new Date(date), "MM/dd/yy");
  };
  const [page, setPage] = useState(1);
  const [rowCount, setRowCount] = useState(50); // default rows per page
  const [first, setFirst] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const onPageChange = (e) => {
    setRowCount(e?.rows);
    setPage(e?.page + 1);
    setFirst(e?.first);
  };

  useEffect(() => {
    setLoading(true);
    fetchTimesheets();
  }, [
    employeeId,
    selectedMonth,
    projectType,
    year,
    periodTypeId,
    page,
    rowCount,
  ]);

  useEffect(() => {
    setSelectedStatus("all");
  }, [employeeId]);

  useEffect(() => {
    getFilteredTimesheets();
  }, [projectType]);

  const fetchTimesheets = async () => {
    try {
      const userToFetch = employeeId || userId;
      const monthToFetch = selectedMonth || null;
      const yearToFetch = year || null;
      const userTypeId = projectType ? projectType : "all";
      const response = await getTimesheets(
        yearToFetch,
        userToFetch,
        monthToFetch,
        currentUserTypeId,
        projectType,
        periodTypeId,
        page,
        rowCount
      );
      const timesheets = response?.timesheetData || [];
      setTotalRecords(response?.timesheetCount);
      const formattedData = timesheets?.map((timesheet) => {
        // For HR (role 2) viewing external user's timesheet, show as approved
        const isExternalUser = timesheet?.userTypeId === 2;
        const isHR =
          userTypeRole?.userTypeRoleId === 2 ||
          userTypeRole?.userTypeRoleId === 3;
        const status = timesheet?.status;

        return {
          periodId: timesheet?.id,
          blobPath: timesheet?.blobPath,
          userName: timesheet?.userName,
          userTypeId: timesheet?.userTypeId,
          periodType: timesheet?.timesheetType,
          projects1: timesheet?.projects,
          createdDate: timesheet?.createdDate,
          monthYear: timesheet?.monthYear,
          weekRange: timesheet?.weekRange,
          approvedBy: timesheet?.approvedBy,
          rejectedBy: timesheet?.rejectedBy,
          periodRange:
            timesheet?.startDate && timesheet?.endDate
              ? `${formatDateForDisplay(
                  timesheet?.startDate
                )} - ${formatDateForDisplay(timesheet?.endDate)}`
              : "",
          totalHours: timesheet?.totalHours || 0,
          status: status,
          rejectionReason: timesheet?.rejectionReason || "",
          documents: timesheet?.documents || [],
          projects: (timesheet?.entries || [])?.map((entry) => ({
            projectName: entry?.projectName || "",
            projectId: entry?.projectId,
            hours: entry?.hours || 0,
            date: entry?.entryDate,
            comments: entry?.comments || "",
          })),
        };
      });
      setTimeSheetData(formattedData);
      setLoading(false);
    } catch (error) {
      appInsights.trackException("Get timeSheetList", { exception: error });
      setTimeSheetData([]);
      console.error("Error fetching timesheets:", error);
      setLoading(false);
    }
  };

  const statusOptions = [
    { label: "All Status", value: "all" },
    { label: "Approved", value: "approved" },
    { label: "Draft", value: "draft" },
    { label: "Pending", value: "pending" },
    { label: "Rejected", value: "rejected" },
  ];

  const statusBodyTemplate = (rowData) => {
    const statusClass = rowData?.status?.toLowerCase();
    return (
      <span className={`status-badge status-${statusClass}`}>
        {rowData?.status}
      </span>
    );
  };

  const confirmApprove = (rowData) => {
    confirmDialog({
      message: "Are you sure you want to approve this timesheet?",
      header: "Confirm Approval",
      icon: "pi pi-check-circle",
      accept: () => handleApprove(rowData),
      acceptClass: "p-button-success",
      rejectClass: "p-button-secondary p-button-text",
    });
  };

  const confirmReject = (rowData) => {
    setSelectedTimesheet(rowData);
    setRejectReason("");
    setShowRejectDialog(true);
  };

  const handleApprove = async (rowData) => {
    try {
      setActionLoading(true);
      await approveTimesheet({
        timeSheetId: rowData?.periodId,
        statusId: 2, // Approved status
      });
      toast?.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Timesheet approved successfully",
      });
      fetchTimesheets(); // Refresh the list
    } catch (error) {
      console.error("Error approving timesheet:", error);
      appInsights.trackException("Post timeSheetList", { exception: error });
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.message || "Something went wrong at Server Side",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason?.trim()) {
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please provide a reason for rejection",
      });
      return;
    }

    try {
      setActionLoading(true);
      await rejectTimesheet({
        timeSheetId: selectedTimesheet?.periodId,
        statusId: 3, // Rejected status
        reason: rejectReason?.trim(), // Ensure we trim any whitespace
      });
      toast?.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Timesheet rejected successfully",
      });
      setShowRejectDialog(false);
      fetchTimesheets(); // Refresh the list
    } catch (error) {
      console.error("Error rejecting timesheet:", error);
      appInsights.trackException("Post timeSheetList", { exception: error });
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.message || "Something went wrong at Server Side",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const actionBodyTemplate = (rowData) => {
    const status = rowData?.status?.toLowerCase();

    if (isManagerViewingEmployee) {
      // For Manager role (4), show both approve and reject only for pending timesheets
      if (userTypeRole?.userTypeRoleId === 4) {
        if (status === "pending") {
          return (
            <div className="action-buttons timesheet_action_buttons">
              <Button
                icon="pi pi-check"
                text
                severity="success"
                className="btn-sm btn-for-approve"
                tooltip="Approve"
                tooltipOptions={{ position: "top" }}
                onClick={() => confirmApprove(rowData)}
                loading={actionLoading}
                disabled={actionLoading}
              />
              <Button
                icon="pi pi-times"
                text
                severity="danger"
                className="btn-for-reject"
                tooltip="Reject"
                tooltipOptions={{ position: "top" }}
                onClick={() => confirmReject(rowData)}
                loading={actionLoading}
                disabled={actionLoading}
              />
            </div>
          );
        }
        return null;
      }

      // For  Accounts role ( 3), show reject button for pending and approved timesheets
      if ([3].includes(userTypeRole?.userTypeRoleId)) {
        if (status === "pending" || status === "approved") {
          return (
            <div className="action-buttons timesheet_action_buttons">
              <Button
                icon="pi pi-times"
                text
                severity="danger"
                className="btn-for-reject"
                onClick={() => confirmReject(rowData)}
                loading={actionLoading}
                disabled={actionLoading}
              />
            </div>
          );
        }
      }
      // Hr  can reject or approve a timesheet
      if ([2].includes(userTypeRole?.userTypeRoleId)) {
        if (status === "approved") {
          return (
            <div className="action-buttons timesheet_action_buttons">
              <Button
                icon="pi pi-times"
                text
                severity="danger"
                className="btn-for-reject"
                tooltip="Reject"
                tooltipOptions={{ position: "top" }}
                onClick={() => confirmReject(rowData)}
                loading={actionLoading}
                disabled={actionLoading}
              />
            </div>
          );
        }
        if (status === "rejected") {
          return (
            <div className="action-buttons timesheet_action_buttons">
              <Button
                icon="pi pi-check"
                text
                severity="success"
                className="btn-sm btn-for-approve"
                onClick={() => confirmApprove(rowData)}
                tooltip="Approve"
                tooltipOptions={{ position: "top" }}
                loading={actionLoading}
                disabled={actionLoading}
              />
            </div>
          );
        }
        if (status === "pending" || status === "rejected") {
          return (
            <div className="action-buttons timesheet_action_buttons">
              <Button
                icon="pi pi-check"
                text
                severity="success"
                className="btn-sm btn-for-approve"
                tooltip="Approve"
                tooltipOptions={{ position: "top" }}
                onClick={() => confirmApprove(rowData)}
                loading={actionLoading}
                disabled={actionLoading}
              />
              <Button
                icon="pi pi-times"
                text
                severity="danger"
                className="btn-for-reject"
                onClick={() => confirmReject(rowData)}
                tooltip="Reject"
                tooltipOptions={{ position: "top" }}
                loading={actionLoading}
                disabled={actionLoading}
              />
            </div>
          );
        }
      }
      return null;
    }

    if (status === "pending") {
      return (
        <div className="action-buttons timesheet_action_buttons">
          <Button
            icon="pi pi-eye"
            text
            severity="info"
            className="btn-for-view"
            tooltip="View"
            tooltipOptions={{ position: "top" }}
            onClick={() =>
              navigate(`/user/self-timesheet/${rowData?.periodId}`)
            }
          />
        </div>
      );
    }
    // Only show edit button for draft or rejected status
    if (status === "draft" || status === "rejected") {
      return (
        <div className="flex">
          <Button
            icon="pi pi-pencil"
            className="p-button-rounded p-button-text"
            onClick={() => handleEdit(rowData)}
            tooltip="Edit"
            tooltipOptions={{ position: "top" }}
          />

          <Button
            icon="pi pi-eye"
            text
            severity="info"
            className="btn-for-view"
            tooltip="View"
            tooltipOptions={{ position: "top" }}
            onClick={() =>
              navigate(`/user/self-timesheet/${rowData?.periodId}`)
            }
          />

          {status === "draft" && (
            <Button
              icon="pi pi-trash"
              className="p-button-rounded p-button-text btn-for-reject "
              severity="danger"
              onClick={() => confirmDelete(rowData)}
              tooltip="Delete"
              tooltipOptions={{ position: "top" }}
            />
          )}
        </div>
      );
    }

    return null;
  };

  const showFile = async (id) => {
    try {
      window.open(id, "_blank");
    } catch (error) {
      console.error("Error opening file:", error);
    }
  };

  const fileBodyTemplate = (rowData) => {
    if (rowData?.documents?.length > 0) {
      return (
        <div className="flex justify-content-start">
          <a
            style={{ cursor: "pointer", color: "blue" }}
            onClick={() => showIcon(rowData)}
          >
            View All
          </a>
          {/* <Button
            icon="pi pi-file"
            text
            severity="info"
            className="btn-for-view"
            tooltip="Download/View"
            tooltipOptions={{ position: "top" }}
            onClick={() => setShowDownloadIcons(true)}
          />
          <Tooltip
            target={`#download-link-${rowData.id}`}
            content="Download"
            position="top"
          /> */}
        </div>
      );
    }
    return null;
  };

  const handleEdit = (rowData) => {
    navigate("/user/edit-timesheet/" + rowData?.periodId, {
      state: {
        mode: "edit",
        timesheetData: rowData,
      },
    });
  };

  const confirmDelete = (rowData) => {
    confirmDialog({
      message: "Are you sure you want to Delete this Time Sheet?",
      header: "Delete Time Sheet!",
      icon: "pi pi-info-circle delete-state-icon1",
      className: "addPaddingForDialogBox",
      acceptClassName: "p-button-danger ",
      accept: () => handleDelete(rowData),
      reject: () => {},
    });
  };

  const handleDelete = async (rowData) => {
    try {
      let result = await deleteTimeSheetDetails(
        JSON.parse(currentUserTypeId),
        rowData?.periodId
      );
      toast?.current?.show({
        severity: "success",
        summary: "Successful",
        detail: `${result}`,
        life: 5000,
        closable: true,
      });

      fetchTimesheets();
    } catch (error) {
      appInsights.trackException("Delete timeSheetList", { exception: error });
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.message || "Something went wrong at Server Side",
        life: 3000,
      });
    }
  };

  const handleAddNew = () => {
    navigate("/user/add-timesheet");
  };

  const viewBodyTemplate = (rowData) => {
    return (
      <div className="action-buttons timesheet_action_buttons">
        <Button
          icon="pi pi-eye"
          text
          severity="info"
          className="btn-for-view"
          tooltip="View"
          tooltipOptions={{ position: "top" }}
          onClick={() =>
            OnlyHr
              ? navigate(`/hr/timesheet/${rowData.periodId}`)
              : navigate(`/user/timesheet/${rowData.periodId}`)
          }
        />
      </div>
    );
  };

  const getFilteredTimesheets = () => {
    let data = timeSheetData;

    // Role-based filtering
    if ([2, 3, 4].includes(userTypeRole?.userTypeRoleId)) {
      if (employeeId) {
        // When viewing employee timesheets, only show pending, approved, and rejected
        data = timeSheetData?.filter((ts) =>
          ["pending", "approved", "rejected"]?.includes(
            ts?.status?.toLowerCase()
          )
        );
      }
      // When viewing own timesheets (no employeeId), show all statuses including draft
    }

    // Apply status filter if selected
    if (selectedStatus && selectedStatus !== "all") {
      data = data?.filter((ts) => ts?.status?.toLowerCase() === selectedStatus);
    }

    if (projectType && projectType !== null) {
      if (projectType?.toLowerCase() === "internal") {
        data = data?.filter((ts) => ts?.userTypeId == 1);
      } else if (projectType?.toLowerCase() === "external") {
        data = data?.filter((ts) => ts?.userTypeId == 2);
      }
    }

    return data;
  };

  const getStatusOptions = () => {
    if ([2, 3, 4].includes(userTypeRole?.userTypeRoleId)) {
      if (employeeId) {
        // When viewing employee timesheets, only show relevant statuses
        return [
          { label: "All Status", value: "all" },
          { label: "Approved", value: "approved" },
          { label: "Pending", value: "pending" },
          { label: "Rejected", value: "rejected" },
        ];
      }
      // When viewing own timesheets, show all statuses including draft
      return [
        { label: "All Status", value: "all" },
        { label: "Approved", value: "approved" },
        { label: "Draft", value: "draft" },
        { label: "Pending", value: "pending" },
        { label: "Rejected", value: "rejected" },
      ];
    }
    return statusOptions;
  };
  const showDialogBox = (entries, rowData) => {
    setSelectedProject(entries); // Stores the list of project entries
    setSelectedRowData(rowData); // Stores the whole row (optional use for name, etc.)
    setVisible(true); // Triggers dialog visibility
  };

  const showIcon = (rowData) => {
    // setSelectedProject(entries); // Stores the list of project entries
    setSelectedRowData(rowData); // Stores the whole row (optional use for name, etc.)
    setShowDownloadIcons(true); // Triggers dialog visibility
  };

  const renderDownloadOrViewIcon = () => {
    return (
      <Dialog
        visible={showDownloadIcons}
        onHide={() => setShowDownloadIcons(false)}
        header="Uploaded Documents"
        modal
        className="p-dialog-sm p-fluid headerForProjectList"
        style={{ width: "50vw", maxWidth: "800px" }}
      >
        <div className="p-3 surface-section">
          <div className="flex flex-wrap gap-2">
            {selectedRowData?.documents.map((entry, index) => (
              <span
                key={index}
                className="bg-white border-round-2xl font-medium text-blue-900 p-2 p-tag"
              >
                <Button
                  icon="pi pi-file"
                  text
                  severity="info"
                  className="btn-for-view"
                  tooltip={`Uploaded on ${formatDateForDisplay(
                    entry?.createdDate
                  )}`}
                  tooltipOptions={{ position: "top" }}
                  onClick={() => showFile(entry?.blobPath)}
                />
                <div
                  className="block"
                  onClick={() => showFile(entry?.blobPath)}
                >
                  {" "}
                  {entry?.fileName}
                </div>
                <Tooltip
                  target={`#download-link-${index}`}
                  content="Download"
                  position="top"
                />
              </span>
            ))}
          </div>
        </div>
      </Dialog>
    );
  };

  const renderDialog = () => {
    if (!selectedProject?.length) return null;

    return (
      <Dialog
        visible={visible}
        onHide={() => setVisible(false)}
        header="Projects List"
        modal
        className="p-dialog-sm p-fluid headerForProjectList"
        style={{ width: "50vw", maxWidth: "800px" }}
      >
        <div className="p-3 surface-section">
          <div className="flex flex-wrap gap-2">
            {selectedProject?.map((entry, index) => (
              <span
                key={index}
                className="surface-ground border-round-2xl font-medium text-blue-900 p-2 p-tag"
              >
                {entry}
              </span>
            ))}
          </div>
        </div>
      </Dialog>
    );
  };

  const statusOptionsFiltered = getStatusOptions();
  // const filteredData = (selectedStatus && selectedStatus !== "all")
  //     ? timeSheetData.filter(item => item.status === selectedStatus)
  //     : timeSheetData;

  const filteredData = getFilteredTimesheets();
  if (loading) {
    return <Spinner />;
  }
  const formatDateFromUTCtoLocal = (createdDate) => {
    if (createdDate != null && createdDate.trim() !== "") {
      // Ensure the string is treated as UTC
      let utcDate = new Date(createdDate + "Z");

      // Format using system's local time zone
      let options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      };

      let localDateTime = new Intl.DateTimeFormat("en-US", options).format(
        utcDate
      );

      return localDateTime; // e.g., "05/13/2025" or system-local date
    } else {
      return "";
    }
  };

  return (
    <div
      className="timesheet-list mx-2 my-2 bg-white p-3"
      style={{ borderRadius: "8px" }}
    >
      {renderDialog()}
      {renderDownloadOrViewIcon()}

      <Toast ref={toast} position="top-center" />
      <Tooltip target=".list-text-ellipsis" />
      <ConfirmDialog className="adminRejectDialog" />
      <Dialog
        className="HrOrAccountantRejectDialog"
        visible={showRejectDialog}
        onHide={() => setShowRejectDialog(false)}
        header="Reject Timesheet"
        modal
        style={{ width: "450px" }}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setShowRejectDialog(false)}
              className="p-button-text"
            />
            <Button
              label="Reject"
              icon="pi pi-check"
              onClick={handleReject}
              loading={actionLoading}
              className="p-button-danger"
            />
          </div>
        }
      >
        <div className="field">
          <label htmlFor="rejectReason" className="block mb-2">
            Reason for Rejection
          </label>
          <InputTextarea
            id="rejectReason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e?.target?.value)}
            rows={3}
            className="w-full"
            autoFocus
          />
        </div>
      </Dialog>
      <div className="list-header">
        <div className="flex align-items-center">
          <span className="flex align-items-center">
            <h2 className="xl:mt-3 mt-xl-0 xl:pr-5 sm:pr-5 pr-1">
              Timesheets List{" "}
            </h2>
          </span>

          {totalRecords && (
            <span className="count-span pl-3 pr-3 xl:mt-2 mt-xl-0 md:pl-4 md:pr-4 p-2">
              {totalRecords}
            </span>
          )}
        </div>
        <div className="header-actions md:pr-4">
          <Dropdown
            value={selectedStatus}
            options={statusOptionsFiltered}
            onChange={(e) => setSelectedStatus(e?.value)}
            placeholder="Select Status"
            className="status-filter"
          />
          {!isManagerViewingEmployee && (
            <Button
              label="Add New Timesheet"
              icon="pi pi-plus"
              className="p-button-primary add-timesheet-btn"
              onClick={handleAddNew}
            />
          )}
        </div>
        <div className="">{downloadBtn()}</div>
      </div>

      <div className="timesheet-table-container">
        <DataTable
          value={filteredData}
          paginator
          rowsPerPageOptions={[5, 10, 25, 50]}
          tableStyle={{ minWidth: "75rem" }}
          scrollable
          scrollHeight="400px"
          breakpoint="960px"
          //TODO:need make it dyanmaic
          totalRecords={totalRecords}
          first={first}
          onPage={onPageChange}
          rows={rowCount}
          lazy
        >
          {(isManagerViewingEmployee || employeeId) && (
            <Column
              field="userName"
              header="Employee Name"
              sortable
              frozen
              style={{
                width: "15%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",

                zIndex: 1, // LOWER z-index for body cells
                position: "sticky",
                left: 0,
                background: "#fff",
              }}
              headerStyle={{
                width: "15%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                zIndex: 3, // HIGHER z-index for header
                position: "sticky",
                top: 0,
                background: "#f8f9fa;",
              }}
              body={(rowData) => (
                <div
                  className="list-text-ellipsis"
                  style={{ maxWidth: "180px" }}
                  data-pr-tooltip={rowData.userName}
                  data-pr-position="top"
                >
                  {rowData?.userName}
                </div>
              )}
            />
          )}

          {OnlyHr && (
            <Column
              field="projects1"
              header="Projects"
              sortable
              style={{ width: "15%" }}
              body={(rowData) => {
                const entry = rowData?.projects1 || [];
                return (
                  <div
                    className="list-text-ellipsis"
                    style={{ maxWidth: "180px", cursor: "pointer" }}
                    onClick={() => showDialogBox(entry, rowData)}
                  >
                    {rowData?.projects1.slice(0, 2).map((entry, index) => (
                      <span key={index}>
                        <u style={{ color: "blue" }}>{entry}</u>
                        {index < rowData?.projects1?.length - 1 ? ", " : ""}
                      </span>
                    ))}
                    {rowData?.projects1?.length > 2 && (
                      <span className="ml-2 text-gray-500">
                        +{rowData?.projects1?.length - 2} more
                      </span>
                    )}
                  </div>
                );
              }}
            />
          )}

          <Column
            field="periodType"
            header="Period Type"
            style={{ width: "12%" }}
          />
          <Column
            field="userTypeId"
            header="Project Type"
            style={{ width: "18%", whiteSpace: "nowrap" }}
            body={(rowData) =>
              rowData["userTypeId"] == 2
                ? "External"
                : rowData["userTypeId"] == 1
                ? "Internal"
                : "All Projects"
            }
          />
          <Column
            field="periodRange"
            header="Period Range"
            sortable
            style={{ width: "18%", whiteSpace: "nowrap" }}
          />
          <Column
            field="totalHours"
            header="Total Hours"
            style={{ width: "8%" }}
          />
          <Column
            field="createdDate"
            header="Created Date"
            style={{ width: "18%", whiteSpace: "nowrap" }}
            body={(rowData) =>
              rowData?.createdDate
                ? formatDateFromUTCtoLocal(rowData?.createdDate)
                : ""
            }
          />

          <Column
            field="approvedBy"
            header="Approved By"
            style={{ width: "18%", whiteSpace: "nowrap" }}
          />
          <Column
            field="rejectedBy"
            header="Rejected By"
            style={{ width: "18%", whiteSpace: "nowrap" }}
          />
          <Column
            field="rejectionReason"
            header="Rejection Reason"
            style={{ width: "18%" }}
            body={(rowData) => (
              <div
                className="list-text-ellipsis"
                style={{ maxWidth: "180px" }}
                data-pr-tooltip={rowData?.rejectionReason}
                data-pr-position="top"
              >
                {rowData?.rejectionReason}
              </div>
            )}
          />
          <Column
            body={fileBodyTemplate}
            header="Document"
            style={{ width: "8%" }}
          />
          <Column
            field="status"
            header="Status"
            body={statusBodyTemplate}
            style={{ width: "10%" }}
          />
          {userTypeRole?.userTypeRoleId !== 5 &&
            !isManagerViewingOwnTimesheet && (
              <Column
                header="View"
                body={viewBodyTemplate}
                style={{ width: "6%" }}
              />
            )}
          <Column
            header="Actions"
            body={actionBodyTemplate}
            style={{ width: "6%" }}
          />
        </DataTable>
      </div>
    </div>
  );
};

export default TimeSheetList;
